// scheduling/services/reportsApi.js
// Client wrapper for /api/rprsReports (Reporting section, Trello #309).
//
// Mirrors poolApi's shape. Kept separate from schedApi because Reporting is its own
// domain Function with its own authorization path (rprs.ReportAccess), and because
// every call here is a read — there are no mutations, so none of schedApi's
// write-side machinery (Outlook reconcile, change broadcasts) applies.

import { fetchWithColdStartRetry } from './schedApi';
import { isLocalHost } from '../utils/localDev';
import { toISO } from '../utils/capacity';

const REPORTS_BASE = '/api/rprsReports';

// Cold-start retry is ON for every call, unlike poolApi's reads. This is a brand-new
// Function, so the first call of a session is the one most likely to hit a cold start,
// and that first call is `fetchMyReportAccess` — the probe that decides whether the
// Reporting tab renders at all. Losing it to a 503 would hide the whole section until
// the user navigated again. schedApi's retry only fires on 502/503/504, so a slow
// report is never re-sent and doubled; only an unreachable one is.
async function call(action, body = {}) {
  const url = `${REPORTS_BASE}?action=${encodeURIComponent(action)}`;
  const r = await fetchWithColdStartRetry(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }, { label: `rprsReports(${action})` });

  let json = null;
  try { json = await r.json(); } catch (_) { /* non-JSON error body */ }

  if (!r.ok) {
    const err = new Error((json && json.error) || `rprsReports(${action}) failed (${r.status})`);
    err.status = r.status;   // callers branch on 403 (no access) vs 500
    throw err;
  }
  return json;
}

// ── capability probe ────────────────────────────────────────────────────────
// Answers for EVERYONE rather than 403-ing, so "no access" is distinguishable from
// an outage. Same contract as poolApi's fetchPoolNavStatus.
// → { ok, reports: string[], isSuperUser, staffId }
export const fetchMyReportAccess = () => call('my_access', {});

// ── reports ─────────────────────────────────────────────────────────────────
// `today` is the CALLER's local date, not the server's. The server runs UTC, so after
// ~7pm Central its "today" is already tomorrow — this report's forward side is
// "scheduled from today on", so letting SQL guess would silently drop or add a day.
// Same reason poolApi ships localToday().
//
// basis: 'all' (default) | 'cy' | 'fy' | 'r12'
//
// includeIntern: hours charged BEFORE a staffer went full time (their internship) are
// excluded by default, per DJB -- an internship is predominantly tax, so leaving it in
// the lifetime total permanently drags the ratio and the person never reads as needing
// tax work. Only this report may pass true; the Staff Suggestions badge never does.
export const fetchGenTrackBalance = ({ basis, includeIntern, today } = {}) =>
  call('gentrack_balance', {
    basis,
    includeIntern: includeIntern === true,
    today: today || toISO(new Date()),
  });

// days: 7 | 30 | 90 (default) | 365 | 0 (all time)
export const fetchSignIns = ({ days } = {}) => call('signins', { days });

// Dimension + bucket metadata for the ad-hoc pickers. Fetched rather than duplicated
// client-side so the list cannot drift from what the server can actually group by.
export const fetchAdhocDimensions = () => call('adhoc_dimensions', {});

// measure: 'scheduled' | 'wip' | 'both'
// `from`/`to` are REQUIRED by the server — pe.TranWIP has no date index, so an
// unbounded charged-hours aggregate would full-scan 6.8M rows.
export const fetchAdhocPivot = ({ measure, groupBy, bucket, from, to, filters } = {}) =>
  call('adhoc_pivot', { measure, groupBy, bucket, from, to, filters });

// Terminated staff still holding scheduled hours (Trello #249).
// `today` is the CALLER's local date: the Function runs UTC, so after ~7pm Central its
// "today" is already tomorrow and a block scheduled for today would drop out of the report.
export const fetchTerminatedStaff = ({ today } = {}) =>
  call('terminated', { today: today || toISO(new Date()) });

// Niche involvement: ranked preferences merged with charged niche hours (Rachel
// Stanley, 2026-09-15). minHours is whitelisted server-side to {0,10,40,100}; anyone
// who RANKED a niche is returned regardless of it.
export const fetchNicheReport = ({ minHours } = {}) =>
  call('niches', { minHours });

// Potentially Unscheduled Hours: max(0, PY prep WIP - CY prep WIP - scheduled) on tax
// return jobs (#252/#387). `returnYear` overrides the legacy month auto-flip (Jan-Oct =
// last calendar year, Nov-Dec = the coming one); the server bounds it and reports back
// which year it actually used, so the UI can state the basis rather than imply one.
//
// `today` is the CALLER's local date. The Function runs UTC, so after ~7pm Central its
// "today" is already tomorrow — which would drop hours scheduled for today out of the
// subtraction AND, on Oct 31 / Dec 31, shift the whole population by a year.
export const fetchUnscheduledReport = ({ returnYear, today } = {}) =>
  call('unscheduled', { returnYear, today: today || toISO(new Date()) });

// 4-Week Lookahead (#252): hours on the jobs a lead manages, Mon-Sun weeks from the Monday
// of `today`, plus stale-hold flags. weeks: 4 | 8 | 12. role: 'both' | 'jm' | 'jp'.
// `leadStaffId` ("view as") is honoured for super-users only — the server ignores it for
// everyone else and binds them to their own resolved staffId. `today` is the caller's local
// date for the same UTC-evening reason as every report above.
export const fetchLookahead = ({ weeks, role, leadStaffId, today } = {}) =>
  call('lookahead', { weeks, role, leadStaffId, today: today || toISO(new Date()) });

// Scheduled vs Actual (#252 PR 2). Deliberately NO `today`: the window is fixed server-side
// (the last full weeks, Central time) and cached per instance, so a caller-supplied date
// could only churn the cache. view: 'me' | 'jobs' | 'firm' (omit = server picks);
// mode: 'job' | 'person'; role: 'both' | 'jm' | 'jp'; weeks: 1 | 2 | 4 | 8.
// `view` and `asStaffId` are honoured only as far as the caller is entitled — the server
// falls back to the caller's own Me view otherwise.
export const fetchSva = ({ view, mode, role, weeks, asStaffId, minCharged } = {}) =>
  call('sva', { view, mode, role, weeks, asStaffId, minCharged });

// ── sign-in beacon ──────────────────────────────────────────────────────────
// Records that this user opened the app. There is no sign-in event in this stack
// otherwise — rprs.AuthLog logs an auth check on EVERY api call and is dominated by the
// 60s notifications poll, so it cannot answer "when did they last sign in".
//
// FIRE AND FORGET, and deliberately never throws: a telemetry write must not produce an
// error toast for someone who just opened the app. Double-guarded against over-counting
// — sessionStorage here (per tab) and a 30-minute window server-side (across tabs), the
// latter being the one that actually holds, since sessionStorage is per-tab by design.
const BEACON_KEY = 'rprs.signin.recorded';

export function recordSignInBeacon() {
  // A local dev session (SWA CLI, often signed in as someone else to test their view) is
  // not that person signing in: it recorded fake sign-ins in rprs.SignIns (found 2026-10-19).
  if (isLocalHost()) return;
  try {
    if (sessionStorage.getItem(BEACON_KEY)) return;
    // Set BEFORE awaiting: two near-simultaneous mounts (StrictMode double-invoke in
    // dev, or a fast remount) must not both fire.
    sessionStorage.setItem(BEACON_KEY, '1');
  } catch (_) {
    // Private mode / storage disabled — fall through and let the server's 30-minute
    // window do the de-duplication on its own.
  }
  call('record_signin', {}).catch(() => { /* telemetry: never surface */ });
}
